import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { BulkUnreserveItemResultDTO, BulkUnreserveResultDTO } from 'src/app/core/models/system/reservation/bulk-unreserve-result.dto';
import { catchError, throwError } from 'rxjs';
import { CompletedModalFormComponent } from 'src/app/forms-modal/_pop-ups/completed-modal-form/completed-modal-form.component';
import { ConfigSystemParams, msSize, OurColors } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ConfirmationModalFormComponent } from 'src/app/forms-modal/_pop-ups/confirmation-modal-form/confirmation-modal-form.component';
import { DevicesService } from 'src/app/core/services/utelize/system/device/devices-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { LastPassKillerService } from 'src/app/core/services/utelize/general/last-pass-killer-service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { WarningModalFormComponent } from 'src/app/forms-modal/_pop-ups/warning-modal-form/warning-modal-form.component';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-unreserve-bulk-modal-form',
  templateUrl: './unreserve-bulk-modal-form.component.html',
  styleUrls: ['./unreserve-bulk-modal-form.component.scss']
})

export class UnreserveBulkModalFormComponent implements AfterViewInit, OnDestroy, OnInit {

  @Input() initialCompanyId: number | undefined;

  @Output() bulkIccidsHaveBeenUnreserved     : EventEmitter<void>=new EventEmitter<void>();
  @Output() unreserveBulkFormHasBeenCancelled: EventEmitter<void>=new EventEmitter<void>();

  @ViewChild('completedModalForm',             { static: false }) completedModalForm!            : CompletedModalFormComponent;
  @ViewChild('unreserveBulkConfirmationModal', { static: false }) unreserveBulkConfirmationModal!: ConfirmationModalFormComponent;
  @ViewChild('unreserveBulkModalFormContent',  { static: false }) unreserveBulkModalFormContent! : TemplateRef<void>;
  @ViewChild('warningModalForm',               { static: false }) warningModalForm!              : WarningModalFormComponent;

  get modalBackGround() { let value: string | boolean=this.configSystemService.getValue(ConfigSystemParams.ModalBackGround, "TRUE");
                          return value=value.toUpperCase()=="STATIC" ? "static"     : (value.toUpperCase()=="TRUE" ? true : false) }

  private readonly iccidHeaderNames: string[]=["iccid", "imei\\iccid", "imei/iccid", "sim", "ssn", "serial", "serial number"];

  private companyId                : number | undefined;
  private unreserveBulkModalFormRef: NgbModalRef | undefined;

  public formTitle            : string="SureSIM Bulk Unreserve";
  public iccids               : string[]=[];
  public results              : BulkUnreserveItemResultDTO[]=[];
  public selectedFile         : string="";
  public summaryFailed        : number=0;
  public summarySucceeded     : number=0;
  public summaryTotal         : number=0;
  public unreserveInProgress  : boolean=false;
  public uploadMessages       : string[]=[];
  public uploadMessagesVisible: boolean=false;

  constructor(
    private configSystemService  : ConfigSystemService,
    private devicesService       : DevicesService,
    private exceptionsService    : ExceptionsService,
    private lastPassKillerService: LastPassKillerService,
    private modalService         : NgbModal
  ) { }

  public ngOnInit(): void {
    try {
      this.companyId=this.initialCompanyId;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.lastPassKillerService.start();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.lastPassKillerService.stop();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Form

  private resetMainVariables(): void {
    try {
      this.iccids               =[];
      this.results              =[];
      this.selectedFile         ="";
      this.summaryFailed        =0;
      this.summarySucceeded     =0;
      this.summaryTotal         =0;
      this.unreserveInProgress  =false;
      this.uploadMessages       =[];
      this.uploadMessagesVisible=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private openModal(): void {
    try {
      this.unreserveBulkModalFormRef=this.modalService.open(this.unreserveBulkModalFormContent, { backdrop: this.modalBackGround,
                                                                                                  centered: true,
                                                                                                  keyboard: false,
                                                                                                  size    : msSize.Large });
    }
    catch(e) {
      this.unreserveBulkFormHasBeenCancelled.emit();
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public cancelForm(): void {
    try {
      this.unreserveBulkFormHasBeenCancelled.emit();
      this.unreserveBulkModalFormRef?.close();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public openForm(companyId: number | undefined): void {
    try {
      this.resetMainVariables();
      this.companyId=companyId ?? this.initialCompanyId;
      this.openModal();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Upload

  private extractIccids(rawRows: any[][]): string[] {
    try {
      if (rawRows==undefined || rawRows.length===0) {
        return [];
      }

      let headerRowIdx: number=-1;
      let iccidColIdx : number=0;

      for (let r=0; r<rawRows.length; r++) {
        const row=rawRows[r];

        if (!Array.isArray(row))
          continue;

        for (let c=0; c<row.length; c++) {
          const cell=(row[c] ?? "").toString().trim().toLowerCase();

          if (this.iccidHeaderNames.indexOf(cell)>-1) {
            headerRowIdx=r;
            iccidColIdx =c;
            break;
          }
        }

        if (headerRowIdx>-1)
          break;
      }

      const values: string[]=[];

      for (let r=headerRowIdx+1; r<rawRows.length; r++) {
        const row=rawRows[r];

        if (!Array.isArray(row))
          continue;

        const value=this.normaliseIccid((row[iccidColIdx] ?? "").toString());

        if (value!="" && values.indexOf(value)===-1) {
          values.push(value);
        }
      }

      return values;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }

  private normaliseIccid(value: string): string {
    try {
      const trimmed=value.trim();

      if (trimmed=="") {
        return "";
      }

      return trimmed.replace(/[^0-9]/g, "");
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  public setUploadMessagesVisible(visible: boolean): void {
    try {
      this.uploadMessagesVisible=visible;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public uploadData(event: any): void {
    try {
      this.resetMainVariables();

      const target: DataTransfer=<DataTransfer>(event.target);

      if (target.files.length!==1)
        throw new Error('UnreserveBulkModalFormComponent.uploadData() - Cannot use multiple files');

      this.selectedFile=target.files[0].name;

      const reader: FileReader=new FileReader();
      reader.readAsArrayBuffer(target.files[0]);

      reader.onload=(e: any) => {
        try {
          const binarystr: string=[...new Uint8Array(e.target.result)]
                                  .map(v => String.fromCharCode(v)).join('');

          const wb     : XLSX.WorkBook =XLSX.read(binarystr, { type: 'binary' });
          const wsname : string    =wb.SheetNames[0];
          const ws     : XLSX.WorkSheet=wb.Sheets[wsname];
          const rawRows: any[][]=XLSX.utils.sheet_to_json(ws, { header: 1, defval: "", raw: false });

          this.iccids=this.extractIccids(rawRows);

          if (this.iccids.length===0) {
            this.uploadMessages       =['No ICCIDs were found in the file. Expected a column headed ICCID (or SSN / Serial Number), or a single column of ICCID values.'];
            this.uploadMessagesVisible=true;
          }
        }
        catch(e) {
          this.exceptionsService.handleException(<Error>e);
        }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Unreserve

  public confirmUnreserve(): void {
    try {
      if (this.iccids.length===0) {
        this.warningModalForm.openForm("Nothing to unreserve", "Upload a file containing a column of ICCIDs first");
        return;
      }

      if (this.companyId==undefined || this.companyId<=0) {
        this.warningModalForm.openForm("No company selected", "Select a company before unreserving");
        return;
      }

      this.unreserveBulkConfirmationModal.openForm("WARNING",
                                                   `Are you sure you want to unreserve ${this.iccids.length} SIM${this.iccids.length===1 ? "" : "s"}?`,
                                                   undefined,
                                                   OurColors.Red,
                                                   OurColors.DarkBlue);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public outcomeBadgeClass(result: BulkUnreserveItemResultDTO): string {
    try {
      return result?.success===true ? "badge bg-success" : "badge bg-danger";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "badge bg-danger";
    }
  }

  public outcomeDescription(result: BulkUnreserveItemResultDTO): string {
    try {
      switch (result?.outcome) {
        case "Unreserved":
          return "Unreserved";
        case "NotFound":
          return "Not found";
        case "NotReserved":
          return "Not reserved";
        case "ReservedToDifferentCompany":
          return "Reserved to a different company";
        case "AlreadyProvisioned":
          return "Already provisioned";
        default:
          return result?.outcome ?? "Unknown";
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "Unknown";
    }
  }

  public yesUnreserveSims(): void {
    try {
      this.unreserveInProgress=true;
      this.results            =[];

      this.devicesService.bulkUnreserveDevices(this.iccids, <number>this.companyId)
      .pipe(
        catchError((error) => {
          this.unreserveInProgress=false;
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)));
        })
      )
      .subscribe({
        next: (data: BulkUnreserveResultDTO) => {
          this.unreserveInProgress=false;
          this.results            =data?.results ?? [];
          this.summaryFailed      =data?.failed ?? 0;
          this.summarySucceeded   =data?.succeeded ?? 0;
          this.summaryTotal       =data?.totalRequested ?? 0;

          if (this.summarySucceeded>0)
            this.bulkIccidsHaveBeenUnreserved.emit();
        },
        error: error => {
          this.unreserveInProgress=false;
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.unreserveInProgress=false;
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
